import { Field } from '@atlaskit/form';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import FieldPicker from '@databases/field-picker/FieldPicker';
import { defaultStoreUtils } from '@databases/redux';
import {
  addChangeToDraft,
  type AddChangeToDraftOptions
} from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  getDatabaseFieldOptional,
  getDatabaseFields
} from '@databases/utils/database/utils';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages.ts';

type PageStatusFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.PAGE_STATUS>;
};

const fieldSettingsAnalyticsOptions = {
  fieldSettingsAnalytics: {
    source: 'fieldContextualMenu',
    fieldType: DatabaseFieldType.PAGE_STATUS
  }
} as AddChangeToDraftOptions;

const toggleWrapperStyles = xcss({
  marginInline: 'space.negative.075'
});

const toggleRowStyles = xcss({
  marginBlock: 'space.negative.050'
});

const isConfluenceLinkField = (
  field: DatabaseField | undefined
): field is DatabaseField<DatabaseFieldType.PAGE_LINK> => {
  return field?.type === DatabaseFieldType.PAGE_LINK;
};

const PageStatusFieldSettings: FunctionComponent<PageStatusFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const [sourceField, sourceFields] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: false
    });
    const selectedSourceField = getDatabaseFieldOptional(
      databaseDraft,
      field.settings.fieldId
    );

    return [
      isConfluenceLinkField(selectedSourceField) ? selectedSourceField : undefined,
      getDatabaseFields(databaseDraft).filter(isConfluenceLinkField)
    ] as const;
  });

  const handleSourceFieldChange = useCallback(
    (value: DatabaseField | undefined) => {
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_FIELD_ID,
            fieldId: field.id,
            settingsFieldId: value?.id
          },
          fieldSettingsAnalyticsOptions
        )
      );
    },
    [contentId, dispatch, field.id]
  );

  const handleAllowEditChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_ALLOW_EDIT,
            fieldId: field.id,
            allowEdit: event.target.checked
          },
          fieldSettingsAnalyticsOptions
        )
      );
    },
    [contentId, dispatch, field.id]
  );

  const allowStatusEditingLabel = formatMessage(messages.allowStatusEditingToggleLabel);

  return (
    <Stack space="space.050">
      <Field
        id="confluence-status-source-field-picker-input"
        isRequired
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {formatMessage(messages.confluenceStatusDisplaySourceLabel)}
          </Text>
        }
        name="confluence-status-source-field"
      >
        {({ fieldProps }) => (
          <FieldPicker
            autoSelectFirst
            fields={sourceFields}
            inputId={fieldProps.id}
            isRequired
            onChange={handleSourceFieldChange}
            spacing="compact"
            value={sourceField}
          />
        )}
      </Field>
      <Field
        id="allow-status-editing-toggle"
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {formatMessage(messages.allowStatusEditingSectionLabel)}
          </Text>
        }
        name="allow-status-editing-toggle"
      >
        {({ fieldProps }) => (
          <Inline
            alignBlock="center"
            space="space.075"
            spread="space-between"
            xcss={toggleRowStyles}
          >
            <Text color="color.text.subtle">{allowStatusEditingLabel}</Text>
            <Box xcss={toggleWrapperStyles}>
              <Toggle
                id={fieldProps.id}
                isChecked={field.settings.allowEdit}
                label={allowStatusEditingLabel}
                onChange={handleAllowEditChange}
              />
            </Box>
          </Inline>
        )}
      </Field>
    </Stack>
  );
};

export default PageStatusFieldSettings;
